import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, profileService } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState(null);

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Fetch current user from API
      const response = await authService.getCurrentUser();
      // Backend returns user data inside 'data' field
      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      // Token invalid/expired - clear auth state
      console.error('Auth check failed:', err);
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login user
  const login = async (credentials) => {
    setError(null);
    try {
      const response = await authService.login(credentials);
      
      if (response.data.success) {
        // Backend returns tokens inside 'data' object
        const { access_token, refresh_token, user: userData } = response.data.data;
        
        // Store tokens
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        setUser(userData);
        setIsAuthenticated(true);
        
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Login failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Login failed';
      setError(errorMessage);
      // Rethrow original error to preserve response data for error handling in components
      throw err;
    }
  };

  // Register new user
  const register = async (data) => {
    setError(null);
    try {
      const response = await authService.register(data);
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Registration failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Verify email with OTP
  const verifyEmail = async (data) => {
    setError(null);
    try {
      const response = await authService.verifyEmail(data);
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Verification failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Verification failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Resend OTP
  const resendOtp = async (email, purpose = 'registration') => {
    setError(null);
    try {
      const response = await authService.resendOtp({ email, purpose });
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to resend OTP';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Forgot password - request OTP
  const forgotPassword = async (email) => {
    setError(null);
    try {
      const response = await authService.forgotPassword(email);
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Failed to send reset OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send reset OTP';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Verify reset OTP
  const verifyResetOtp = async (data) => {
    setError(null);
    try {
      const response = await authService.verifyResetOtp(data);
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Invalid OTP');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Invalid OTP';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Reset password
  const resetPassword = async (data) => {
    setError(null);
    try {
      const response = await authService.resetPassword(data);
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Password reset failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Password reset failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Logout user
  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  // Update user data
  const updateUser = async (userData) => {
    try {
      const response = await profileService.updateProfile(userData);
      
      if (response.data.success) {
        const updatedUser = { ...user, ...userData };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Update failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Update failed';
      throw new Error(errorMessage);
    }
  };

  // Change password
  const changePassword = async (data) => {
    try {
      const response = await profileService.changePassword(data);
      
      if (response.data.success) {
        return { success: true, data: response.data };
      } else {
        throw new Error(response.data.error || response.data.message || 'Password change failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Password change failed';
      throw new Error(errorMessage);
    }
  };

  // Refresh user data from API
  const refreshUser = async () => {
    try {
      const response = await authService.getCurrentUser();
      if (response.data.success && response.data.data) {
        const userData = response.data.data;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return userData;
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  // Clear error
  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated,
        error,
        login,
        register,
        verifyEmail,
        resendOtp,
        forgotPassword,
        verifyResetOtp,
        resetPassword,
        logout,
        updateUser,
        changePassword,
        checkAuth,
        refreshUser,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
